import { useViewerHasAtLeastAdminRights } from "@/lib/rights/use-viewer-has-right";
import useTranslation from "@babackapp/translations/use-translation";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@babackapp/ui/components/accordion";
import { Badge } from "@babackapp/ui/components/badge";
import NumberFlow from "@number-flow/react";
import { ReturnRequestLineItemDto } from "@babackapp/private-api-client/types";
import { ArticleItem } from "../article-item/article-item";
import TextAreaWithSuggestions from "@/pages/(app)/returns/refund/validate/text-area-with-suggestion";
import { useLineItemRejectReasons } from "@/pages/(app)/returns/refund/hooks/use-line-item-reject-reason";
import { useMemo } from "react";
import { useRejectLineItemsListSections } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-list-section";
import useRejectLineItemsQuantitiesSummary from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-quantities-summary";
import {
  useRejectedByLogistician,
  useSetRejectedByLogistician,
} from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-state-context";
import { useRejectLineItem } from "@/pages/(app)/returns/refund/hooks/use-reject-line-items-context";

export default function ArticlesRejectedByLogistician() {
  const t = useTranslation();
  const { itemsRejectedByLogistician } = useRejectLineItemsListSections();
  const hasPermissionToView = useViewerHasAtLeastAdminRights();
  const summary = useRejectLineItemsQuantitiesSummary({
    state: "rejected-by-logistician",
  });

  if (summary.max <= 0 || !hasPermissionToView) {
    return null;
  }

  return (
    <AccordionItem value="rejectedByLogistician">
      <AccordionTrigger>
        <span>
          {t("returnDetail.confirmModal.rejectedByLogistician")}
          <Badge variant="secondary" className="ml-2">
            <NumberFlow value={summary.selected} suffix={` / ${summary.max}`} />
          </Badge>
        </span>
      </AccordionTrigger>
      <AccordionContent>
        <div className="border-border border rounded-md flex-col gap-4 divide-y overflow-hidden">
          {itemsRejectedByLogistician.map((lineItem) => (
            <RejectedByLogisticianArticle
              key={lineItem.line_item_id}
              lineItem={lineItem}
            />
          ))}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}

function RejectedByLogisticianArticle({
  lineItem,
}: {
  lineItem: ReturnRequestLineItemDto;
}) {
  const { item, updateResolution, updateVariant } = useRejectLineItem(lineItem);
  const quantityValue = useRejectedByLogistician(lineItem.line_item_id);
  const updateQuantity = useSetRejectedByLogistician(lineItem.line_item_id);

  const { addLineItemReason, lineItemsReason } = useLineItemRejectReasons();
  const editineItemReason = addLineItemReason(lineItem.line_item_id);

  const selectLineItemReason = useMemo(() => {
    return lineItemsReason[lineItem.line_item_id]?.reason;
  }, [lineItemsReason, lineItem.line_item_id]);

  return (
    <>
      <ArticleItem
        lineItem={item}
        quantity={quantityValue.selectedQuantity}
        maxQuantity={quantityValue.maxQuantity}
        onChangeQuantity={updateQuantity}
        onChangeVariant={updateVariant}
        onChangeResolution={updateResolution}
        hideStatus
        hideResolution
      />
      {quantityValue.selectedQuantity > 0 && (
        <TextAreaWithSuggestions
          onChangeReason={editineItemReason}
          reason={selectLineItemReason}
        />
      )}
    </>
  );
}
